import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Hotel, Room, HotelFormData, RoomFormData } from '@/types/hotel';
import { toast } from 'sonner';

export const useHotels = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHotels = async () => {
    try {
      const { data, error } = await supabase
        .from('hotels')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHotels(data || []);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      toast.error('خطأ في جلب بيانات الفنادق');
    }
  };

  const fetchRooms = async () => {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select(`
          *,
          hotel:hotels!fk_rooms_hotel_id(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // تحويل البيانات للتأكد من التوافق مع النوع المطلوب
      const transformedRooms = data?.map(room => ({
        id: room.id,
        hotel_id: room.hotel_id,
        name: room.name,
        name_en: room.name_en || undefined,
        selling_price: room.selling_price || undefined,
        buying_price: room.buying_price || undefined,
        currency: room.currency || undefined,
        week_prices: room.week_prices || {},
        from_date: room.from_date || undefined,
        to_date: room.to_date || undefined,
        is_weekly_pricing: room.is_weekly_pricing || false,
        created_at: room.created_at,
        updated_at: room.updated_at,
        description: (room as any).description || undefined,
        amenities: (room.amenities as string[]) || [],
        room_images: (room.room_images as string[]) || [],
        hotel: room.hotel && typeof room.hotel === 'object' && !Array.isArray(room.hotel) && !('error' in room.hotel) ? (room.hotel as Hotel) : undefined
      })) || [];
      
      setRooms(transformedRooms as Room[]);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('خطأ في جلب بيانات الغرف');
    } finally {
      setLoading(false);
    }
  };

  const saveHotel = async (formData: HotelFormData, editingHotel: Hotel | null = null, imageFile: File | null = null): Promise<boolean> => {
    try {
      let hotelData = { ...formData };

      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `public/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('hotel_images')
          .upload(filePath, imageFile);

        if (uploadError) {
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('hotel_images')
          .getPublicUrl(filePath);

        hotelData.image_url = urlData.publicUrl;
      }

      if (editingHotel) {
        const { error } = await supabase
          .from('hotels')
          .update({
            ...hotelData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingHotel.id);

        if (error) throw error;
        toast.success('تم تحديث الفندق بنجاح');
      } else {
        // If this hotel is set as default, unset other defaults
        if (formData.is_default) {
          await supabase
            .from('hotels')
            .update({ is_default: false })
            .neq('id', '00000000-0000-0000-0000-000000000000'); // Dummy ID to update all
        }

        const { error } = await supabase
          .from('hotels')
          .insert([hotelData]);

        if (error) throw error;
        toast.success('تم إضافة الفندق بنجاح');
      }

      await fetchHotels();
      return true;
    } catch (error) {
      console.error('Error saving hotel:', error);
      toast.error('خطأ في حفظ بيانات الفندق');
      return false;
    }
  };

  const saveRoom = async (formData: RoomFormData, editingRoom: Room | null = null): Promise<boolean> => {
    try {
      if (editingRoom) {
        const { error } = await supabase
          .from('rooms')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingRoom.id);

        if (error) throw error;
        toast.success('تم تحديث الغرفة بنجاح');
      } else {
        const { error } = await supabase
          .from('rooms')
          .insert([formData]);

        if (error) throw error;
        toast.success('تم إضافة الغرفة بنجاح');
      }

      await fetchRooms();
      return true;
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error('خطأ في حفظ بيانات الغرفة');
      return false;
    }
  };

  const deleteHotel = async (hotelId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('hotels')
        .delete()
        .eq('id', hotelId);

      if (error) throw error;
      toast.success('تم حذف الفندق بنجاح');
      await fetchHotels();
      await fetchRooms(); // Refresh rooms as they might be affected
      return true;
    } catch (error) {
      console.error('Error deleting hotel:', error);
      toast.error('خطأ في حذف الفندق');
      return false;
    }
  };

  const deleteRoom = async (roomId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('rooms')
        .delete()
        .eq('id', roomId);

      if (error) throw error;
      toast.success('تم حذف الغرفة بنجاح');
      await fetchRooms();
      return true;
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('خطأ في حذف الغرفة');
      return false;
    }
  };

  useEffect(() => {
    fetchHotels();
    fetchRooms();
  }, []);

  return {
    hotels,
    rooms,
    loading,
    saveHotel,
    saveRoom,
    deleteHotel,
    deleteRoom,
    fetchHotels,
    fetchRooms
  };
};
